import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Select } from "semantic-ui-react";
import { Priceable } from "../../Types";
import { Quotation } from "../../../../stores/CoursePreviewStore/Types";
import { canShowCoursePaymentToggle } from "../../service";

type Course = {
  key: string;
  value: string;
  label: string;
};

interface Props {
  courses: Course[];
  selectedCourse: string;
  priceable: Priceable;
  activeQuote: Quotation;
  quotes: Quotation[];
  subscriptionActiveQuote: Quotation;
  onCourseChange: (e: any, data: any) => void;
  handleChange: (key: string, value: any) => void;
  canChangeCourse?: boolean;
}

const Course: React.FC<Props> = ({
  courses,
  priceable,
  activeQuote,
  quotes,
  subscriptionActiveQuote,
  onCourseChange,
  handleChange,
  selectedCourse,
  canChangeCourse = true,
}) => {
  const [changeCourse, setChangeCourse] = useState(false);
  const fullPaymentQuote = quotes?.find(
    (quote) => quote && !quote.course_payment_plan,
  );
  const paymentPlanQuote = quotes?.find((quote) => quote?.course_payment_plan);
  const optionClassName = (isActive) => `${isActive ? "active" : ""}`.trim();
  const isPaymentPlanActive = Boolean(activeQuote?.course_payment_plan);
  const isFullPaymentActive = Boolean(
    activeQuote && !activeQuote.course_payment_plan,
  );
  const showFullPaymentSaving = Boolean(
    isFullPaymentActive && activeQuote?.discounted,
  );
  const showPaymentToggle = canShowCoursePaymentToggle({
    priceable,
    fullPaymentQuote,
    paymentPlanQuote,
  });
  // Nothing to upsell to someone already paying nothing, and the nudge quotes
  // the monthly pass price from a quote the same voucher has zeroed -- so it
  // offered "40+ courses from £0 a month", which is not a price we sell.
  const isFreeCourse = activeQuote?.fractional === 0;

  const showDropdown =
    !priceable || (canChangeCourse && (changeCourse || !selectedCourse));

  return (
    <>
      <p className="subtitle" id="formSubtitle">
        You're one form away from your first lesson.
      </p>
      {showDropdown ? (
        <div className="course-selector" id="courseDropdown">
          <span className="cs-label" id="cs-label">
            Select your course
          </span>

          <Select
            placeholder="Select a course..."
            className="courses-dropdown"
            fluid
            search
            selection
            disabled={!canChangeCourse}
            onChange={(e, data) => onCourseChange(e, data.value)}
            value={selectedCourse}
            options={courses}
          />
        </div>
      ) : (
        // No picture here: the course's tile is on the plan card beside it,
        // and the same picture twice looked odd (Chris, 29 Oct 2026).
        <div className="selected-course" id="selectedCourseCard">
          <div className="details">
            <h4 id="selectedCourseTitle">{priceable?.name}</h4>
            <p id="selectedCourseMeta">
              {priceable?.brand_name} · {priceable?.number_of_lessons} lessons ·
              CPD Certified
            </p>
          </div>

          {canChangeCourse && (
            <button
              className="change-btn"
              type="button"
              onClick={() => setChangeCourse(true)}
            >
              Change
            </button>
          )}
        </div>
      )}
      {showPaymentToggle && (
        <div
          className="payment-toggle"
          id="payToggle"
          role="tablist"
          aria-label="Payment frequency"
        >
          <button
            className={optionClassName(isPaymentPlanActive)}
            data-mode="spread"
            role="tab"
            aria-selected={isPaymentPlanActive}
            type="button"
            disabled={!paymentPlanQuote}
            onClick={() => handleChange("activeQuote", paymentPlanQuote)}
          >
            <span className="label-text">Pay in 3</span>{" "}
            <span className="save-badge">Start for less</span>
          </button>
          <button
            data-mode="full"
            role="tab"
            aria-selected={isFullPaymentActive}
            type="button"
            className={optionClassName(isFullPaymentActive)}
            disabled={!fullPaymentQuote}
            onClick={() => handleChange("activeQuote", fullPaymentQuote)}
          >
            <span className="label-text">Pay in full</span>{" "}
            <span className="save-badge">Save</span>
          </button>
        </div>
      )}
      {showPaymentToggle && showFullPaymentSaving && (
        <div className="savings-callout" id="courseSavingsCallout">
          <span className="tag">
            <svg aria-hidden="true">
              <use href="#icon-champagne" />
            </svg>
            <span className="copy">
              You'll save a rather marvellous{" "}
              <strong>
                {activeQuote.currency}
                {activeQuote.discount_amount}
              </strong>
              .
            </span>
          </span>
        </div>
      )}
      {!isFreeCourse && (
        <div className="upgrade-nudge" id="upgradeNudge">
          <p id="upgradeNudgeCopy">
            From only{" "}
            <strong>
              {subscriptionActiveQuote?.currency}
              {subscriptionActiveQuote?.amount} a month
            </strong>
            , get all 40+ Mini MBA courses instead of just one.
          </p>

          <Link
            to="/passes/month/pay"
            className="nudge-btn"
            id="upgradeNudgeBtn"
            type="button"
          >
            See All Courses Pass
          </Link>
        </div>
      )}
    </>
  );
};

export default Course;
